import {
    Avatar,
    Badge,
    Box,
    Divider,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Switch,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
} from '@chakra-ui/react';
import {
    CalendarIcon,
    CheckIcon,
    CloseIcon,
    DeleteIcon,
    DownloadIcon,
    EditIcon,
    SettingsIcon,
    ViewIcon,
} from '@chakra-ui/icons';
import React, { memo } from 'react';

import ErrorMessage from 'components/ErrorMessage';
import { EventType } from 'types/models';
import { Link } from 'react-router-dom';
import { renderDateAsDayMonth } from 'utils/dateUtils';
import renderEventName from 'utils/renderEventName';

export interface EventsTableProps {
    events: EventType[];
    path: string;
    onDeleteClick: (id: number) => void;
    onParticipantsClick: (event: EventType) => void;
    // exibe a coluna "Ativo" com toggle e o item "Alterar Edição" no menu.
    onActiveChange?: (id: number, newStatus: boolean) => void;
    // exibe a coluna "Visível" com toggle.
    onVisibleChange?: (id: number, newStatus: boolean) => void;
}

const EventsTable: React.FC<EventsTableProps> = memo(
    ({ events, path, onDeleteClick, onParticipantsClick, onActiveChange, onVisibleChange }: EventsTableProps) => {
        // Só renderiza as colunas/itens de status se ao menos um handler for fornecido.
        // Isso mantém o componente limpo para a listagem de edições anteriores,
        // que não precisa desses controles.
        const hasStatusCols = Boolean(onActiveChange || onVisibleChange);

        if (events.length === 0) {
            return <ErrorMessage>Nenhum resultado encontrado.</ErrorMessage>;
        }

        return (
            <TableContainer
                w="100%"
                overflowX="auto"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                shadow="md"
                bg="white"
                mt={4}
            >
                <Table variant="simple" size="lg">
                    <Thead bg="gray.50">
                        <Tr>
                            <Th fontSize="xl" py={6} whiteSpace="nowrap">Edição</Th>
                            <Th fontSize="xl" py={6} whiteSpace="nowrap">Área</Th>
                            <Th fontSize="xl" py={6} whiteSpace="nowrap">Período</Th>
                            <Th fontSize="xl" py={6} whiteSpace="nowrap">Organizadores</Th>
                            <Th fontSize="xl" py={6} whiteSpace="nowrap">Certificados</Th>
                            {/* Colunas de status: só aparecem em ListEvents (eventos ativos) */}
                            {onActiveChange && (
                                <Th fontSize="xl" py={6} textAlign="center" whiteSpace="nowrap">Ativo</Th>
                            )}
                            {onVisibleChange && (
                                <Th fontSize="xl" py={6} textAlign="center" whiteSpace="nowrap">Visível</Th>
                            )}
                            <Th fontSize="xl" py={6} textAlign="center" whiteSpace="nowrap">Ações</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {events.map((event) => (
                            <Tr
                                key={event.id}
                                _hover={{ bg: 'blue.50' }}
                                transition="all 0.2s"
                                borderBottomWidth="2px"
                                borderColor="gray.200"
                            >
                                <Td whiteSpace="nowrap" py={6}>
                                    <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                                        {renderEventName(event)}
                                    </Text>
                                </Td>

                                <Td whiteSpace="nowrap" py={6}>
                                    {event.eventArea?.sigla ? (
                                        <Badge
                                            colorScheme="blue"
                                            variant="subtle"
                                            fontSize="xl"
                                            px={3}
                                            py={1}
                                            borderRadius="md"
                                        >
                                            {event.eventArea.sigla}
                                        </Badge>
                                    ) : (
                                        <Text color="gray.400" fontSize="xl">-</Text>
                                    )}
                                </Td>

                                <Td whiteSpace="nowrap" py={6}>
                                    <HStack color="gray.700" fontWeight="bold" fontSize="2xl">
                                        <CalendarIcon color="blue.600" />
                                        <Text>
                                            {renderDateAsDayMonth(event.startDate)}
                                            {' - '}
                                            {event.endDate
                                                ? renderDateAsDayMonth(event.endDate)
                                                : 'Sem data final'}
                                        </Text>
                                    </HStack>
                                </Td>

                                <Td whiteSpace="nowrap" py={6}>
                                    {event.responsibleUsers && event.responsibleUsers.length > 0 ? (
                                        <VStack align="start" spacing={3}>
                                            {event.responsibleUsers.map((r) => (
                                                <HStack key={r.id}>
                                                    <Avatar size="sm" name={r.name} />
                                                    <Text fontSize="xl" fontWeight="medium" color="gray.700">
                                                        {r.name}
                                                    </Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    ) : (
                                        <Text color="gray.400" fontSize="xl">-</Text>
                                    )}
                                </Td>

                                <Td whiteSpace="nowrap" py={6}>
                                    {event.readyForCertificate ? (
                                        <Badge
                                            colorScheme="green"
                                            px={3}
                                            py={2}
                                            fontSize="xl"
                                            borderRadius="xl"
                                            display="inline-flex"
                                            alignItems="center"
                                            gap={2}
                                        >
                                            <CheckIcon boxSize={4} /> Prontos
                                        </Badge>
                                    ) : (
                                        <Badge
                                            colorScheme="orange"
                                            px={3}
                                            py={2}
                                            fontSize="xl"
                                            borderRadius="xl"
                                            display="inline-flex"
                                            alignItems="center"
                                            gap={2}
                                        >
                                            <CloseIcon boxSize={4} /> Pendentes
                                        </Badge>
                                    )}
                                </Td>

                                {/* Coluna "Ativo": toggle que chama onActiveChange ao ser clicado */}
                                {onActiveChange && (
                                    <Td textAlign="center" whiteSpace="nowrap" py={6}>
                                        <Switch
                                            colorScheme="blue"
                                            size="lg"
                                            isChecked={event.statusActive}
                                            onChange={() => onActiveChange(event.id, !event.statusActive)}
                                        />
                                    </Td>
                                )}

                                {/* Coluna "Visível": toggle que chama onVisibleChange ao ser clicado */}
                                {onVisibleChange && (
                                    <Td textAlign="center" whiteSpace="nowrap" py={6}>
                                        <Switch
                                            colorScheme="blue"
                                            size="lg"
                                            isChecked={event.statusVisible}
                                            onChange={() => onVisibleChange(event.id, !event.statusVisible)}
                                        />
                                    </Td>
                                )}

                                <Td textAlign="center" whiteSpace="nowrap" py={6}>
                                    <Menu placement="bottom-end">
                                        <MenuButton
                                            as={IconButton}
                                            icon={<SettingsIcon boxSize={6} />}
                                            variant="outline"
                                            borderWidth="2px"
                                            colorScheme="blue"
                                            aria-label="Opções"
                                            size="lg"
                                            _hover={{ bg: 'blue.100' }}
                                        />
                                        <MenuList
                                            shadow="2xl"
                                            zIndex={10}
                                            minW="260px"
                                            borderRadius="xl"
                                            py={2}
                                            textAlign="left"
                                        >
                                            <MenuItem
                                                py={4}
                                                px={4}
                                                fontSize="xl"
                                                icon={<ViewIcon boxSize={6} mr={2} />}
                                                as={Link}
                                                to={`/evento/${event.eventCategory?.url_src}/${event.id}`}
                                            >
                                                Ir para Evento
                                            </MenuItem>

                                            {/* "Alterar Edição" só existe para eventos ativos, não para edições anteriores */}
                                            {hasStatusCols && (
                                                <MenuItem
                                                    py={4}
                                                    px={4}
                                                    fontSize="xl"
                                                    icon={<EditIcon boxSize={6} mr={2} />}
                                                    as={Link}
                                                    to={`${path}/${event.id}/alterar`}
                                                >
                                                    Alterar Edição
                                                </MenuItem>
                                            )}
                                            <MenuItem
                                                py={4}
                                                px={4}
                                                fontSize="xl"
                                                icon={<CalendarIcon boxSize={6} mr={2} />}
                                                as={Link}
                                                to={`${path}/${event.id}/programacao`}
                                            >
                                                Programação
                                            </MenuItem>
                                            <MenuItem
                                                py={4}
                                                px={4}
                                                fontSize="xl"
                                                icon={<DownloadIcon boxSize={6} mr={2} />}
                                                onClick={() => onParticipantsClick(event)}
                                            >
                                                Participantes
                                            </MenuItem>
                                            <Divider my={3} />
                                            <Tooltip
                                                label={
                                                    !('canExclude' in event)
                                                        ? 'Somente administrador pode apagar um evento'
                                                        : (!event.canExclude
                                                            ? 'Há atividades vinculadas a este evento.'
                                                            : '')
                                                }
                                                hasArrow
                                                placement="left"
                                                fontSize="lg"
                                                p={3}
                                                borderRadius="md"
                                            >
                                                <Box>
                                                    <MenuItem
                                                        py={4}
                                                        px={4}
                                                        fontSize="xl"
                                                        icon={<DeleteIcon boxSize={6} mr={2} />}
                                                        color="red.600"
                                                        fontWeight="bold"
                                                        isDisabled={!event.canExclude}
                                                        onClick={() => onDeleteClick(event.id)}
                                                    >
                                                        Excluir Edição
                                                    </MenuItem>
                                                </Box>
                                            </Tooltip>
                                        </MenuList>
                                    </Menu>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </TableContainer>
        );
    },
);

EventsTable.displayName = 'EventsTable';

export default EventsTable;
